// Ported from hotwire-flow (MIT, @hotwirebits/flow) into conek-ui. The engine
// logic is upstream's; identifiers/events/classes follow the conek contract in
// docs/superpowers/specs/2026-09-09-flow-graph-components-design.md §2.
import { Controller } from "@hotwired/stimulus"

// Minimap overview of the graph.
export default class extends Controller {
  static outlets = ["ui--flow"]

  static values = {
    // M6: token defaults, aligned with MinimapComponent's kwargs — never hex.
    nodeColor: { type: String, default: "hsl(var(--muted-foreground))" },
    nodeStrokeColor: { type: String, default: "hsl(var(--foreground-pale))" },
    viewportColor: { type: String, default: "hsl(var(--primary) / 0.1)" },
    viewportStroke: { type: String, default: "hsl(var(--primary))" },
    pannable: { type: Boolean, default: true }
  }

  connect() {
    this.element.classList.add("ui-flow-minimap")

    // Persistent render state (perf): the svg, per-node rects, cached dims and
    // bounds live across frames — hotspots (nodemove, viewportchange) mutate
    // attributes on existing rects instead of rebuilding innerHTML.
    this._svg = null
    this._vpRect = null
    this._vpMask = null           // evenodd dim-outside-viewport overlay (UX audit)
    this._mmW = 0
    this._mmH = 0
    this._nodeRects = new Map()  // nodeId → <rect>
    this._nodeDims = new Map()   // nodeId → { w, h } (refreshed on full render)
    this._bounds = null          // { minX, minY, scale } or null
    // W3-B OPT: node ids whose dims changed since the last geometry flush.
    // When the recomputed bounds are UNCHANGED (the common mid-drag case —
    // a moved node inside the existing extent), only those rects rewrite:
    // 1 attribute write per moved node instead of 4 per node in the graph.
    // A bounds change falls back to the full rewrite (dragging outside the
    // current extent scales every rect — no shortcut there).
    this._dirtyNodes = new Set()
    this._frameRaf = null
    this._pending = false

    // Chrome-insets contract (UX audit): the SSR inline style (the canvas
    // slot's MINIMAP_POSITION_STYLES) keeps the 16px baseline; JS lifts the
    // anchored side by the engine's measured strip inset so the minimap
    // clears the status bar. Base offsets captured once — re-applies rewrite
    // base + inset.
    this._baseBottom = this._parsePx(this.element.style.bottom)
    this._baseTop = this._parsePx(this.element.style.top)
    this._applyChromeLift()
    this._onChromeChange = () => this._applyChromeLift()
    this._onWindowResize = () => this._scheduleChromeLift()
    document.addEventListener("ui--flow:chromechange", this._onChromeChange)
    window.addEventListener("resize", this._onWindowResize)
    this._announceChrome()

    this._render()

    // viewportchange moves ONLY the viewport rect — no node work.
    this._onViewport = () => this._scheduleViewport()
    // A node move dirties one stored position → geometry-only flush, no rect
    // rebuild. The detail's x/y are the node's RELATIVE left/top (the engine
    // emits exactly what _moveNode wrote), but _nodeDims stores ABSOLUTE
    // flow-space positions (the full render's _nodeFlowPosition walks node
    // ancestors) — so the fast path shifts the event coords by the element's
    // ancestor offset (M2), then pushes the resulting delta onto every node
    // descendant (M1): children ride a dragged parent's DOM subtree without
    // emitting nodemove events of their own, and their rects would otherwise
    // go stale for the whole drag. The event's x/y stay authoritative (a
    // synthetic nodemove without a DOM write — tests, host-driven updates —
    // still lands), the ancestor walk only re-bases them.
    this._onNodeMove = (e) => {
      const detail = e?.detail || {}
      if (detail.nodeId && this._nodeDims.has(detail.nodeId)) {
        const d = this._nodeDims.get(detail.nodeId)
        const el = this._flow()?._nodeElement?.(detail.nodeId)
        if (el && detail.x !== undefined && detail.y !== undefined) {
          const base = this._ancestorFlowOffset(el)
          const absX = detail.x + base.x, absY = detail.y + base.y
          const dx = absX - d.x, dy = absY - d.y
          d.x = absX
          d.y = absY
          this._dirtyNodes.add(detail.nodeId)
          if (dx !== 0 || dy !== 0) {
            el.querySelectorAll("[data-ui--flow-node]").forEach(child => {
              const cd = this._nodeDims.get(child.dataset["ui-FlowNode"])
              if (cd) { cd.x += dx; cd.y += dy; this._dirtyNodes.add(child.dataset["ui-FlowNode"]) }
            })
          }
        } else {
          // Element unresolvable (mid-teardown, foreign markup): keep the
          // historical raw write rather than dropping the update.
          if (detail.x !== undefined) d.x = detail.x
          if (detail.y !== undefined) d.y = detail.y
          this._dirtyNodes.add(detail.nodeId)
        }
      }
      this._scheduleGeometry()
    }
    // Membership changes (rare) rebuild the rect map.
    this._onMembership = () => this._scheduleRender()
    // S13: noderesize carries nodeId + flow-unit geometry — a filtered dims
    // update + geometry flush covers the common case; anything else (older
    // emitters, unknown node) falls back to a full render.
    // A3: the event's x/y are the element's own RELATIVE left/top (the
    // resizer emits exactly what it wrote — node_resizer_controller.js), but
    // _nodeDims stores ABSOLUTE flow-space positions, so a mid-gesture
    // N/NW/W/S/SW resize of a CHILD node would misplace its rect until the
    // next nodemove. Re-base through the ancestor offset exactly like
    // _onNodeMove above (M2); width/height keep the direct assign (sizes
    // have no origin). No child-delta push here (contrast M1): for E/SE/S
    // resizes (origin fixed) resizing a parent does not move its children's
    // absolute positions. The other five handles (N, NE, W, SW, NW) are
    // origin-moving — the resizer also moves the parent's left/top
    // (`includes("n")` moves top, `includes("w")` moves left), so the
    // parent-relative children shift by the same absolute delta and their
    // minimap rects lag mid-gesture — it self-heals at pointerup, when the
    // resizer's moveNode → nodemove runs the M2 ancestor re-base + the M1
    // child-delta push.
    this._onResize = (e) => {
      const detail = e?.detail || {}
      if (detail.nodeId && this._nodeDims.has(detail.nodeId)) {
        const d = this._nodeDims.get(detail.nodeId)
        if (detail.width !== undefined) d.w = detail.width
        if (detail.height !== undefined) d.h = detail.height
        const el = this._flow()?._nodeElement?.(detail.nodeId)
        if (el && detail.x !== undefined && detail.y !== undefined) {
          const base = this._ancestorFlowOffset(el)
          d.x = detail.x + base.x
          d.y = detail.y + base.y
        } else {
          // Element unresolvable (mid-teardown, foreign markup): keep the
          // historical raw write rather than dropping the update.
          if (detail.x !== undefined) d.x = detail.x
          if (detail.y !== undefined) d.y = detail.y
        }
        this._dirtyNodes.add(detail.nodeId)
        this._scheduleGeometry()
      } else {
        this._scheduleRender()
      }
    }

    document.addEventListener("ui--flow:viewportchange", this._onViewport)
    document.addEventListener("ui--flow:nodemove", this._onNodeMove)
    document.addEventListener("ui--flow:nodesadd", this._onMembership)
    document.addEventListener("ui--flow:nodedelete", this._onMembership)
    document.addEventListener("ui--flow:restore", this._onMembership)
    document.addEventListener("ui--flow:noderesize", this._onResize)

    if (this.pannableValue) {
      this._onPanDown = (e) => this._handlePan(e)
      this.element.addEventListener("pointerdown", this._onPanDown)
    }
  }

  disconnect() {
    document.removeEventListener("ui--flow:viewportchange", this._onViewport)
    document.removeEventListener("ui--flow:nodemove", this._onNodeMove)
    document.removeEventListener("ui--flow:nodesadd", this._onMembership)
    document.removeEventListener("ui--flow:nodedelete", this._onMembership)
    document.removeEventListener("ui--flow:restore", this._onMembership)
    document.removeEventListener("ui--flow:noderesize", this._onResize)
    document.removeEventListener("ui--flow:chromechange", this._onChromeChange)
    window.removeEventListener("resize", this._onWindowResize)
    if (this._onPanDown) this.element.removeEventListener("pointerdown", this._onPanDown)
    if (this._frameRaf) { cancelAnimationFrame(this._frameRaf); this._frameRaf = null; this._pending = false }
    if (this._liftRaf) { cancelAnimationFrame(this._liftRaf); this._liftRaf = null }
    this._announceChrome()
  }

  _parsePx(value) {
    const n = parseFloat(value)
    return Number.isFinite(n) ? n : null
  }

  // Rewrite the inline bottom/top the canvas slot baked (see
  // canvas_component.rb MINIMAP_POSITION_STYLES — the Ruby table stays the
  // no-JS baseline; the engine's insets are the runtime source of truth).
  _applyChromeLift() {
    const insets = this._flow()?.getChromeInsets?.() || { top: 0, bottom: 0 }
    if (this._baseBottom !== null) this.element.style.bottom = `${this._baseBottom + insets.bottom}px`
    if (this._baseTop !== null) this.element.style.top = `${this._baseTop + insets.top}px`
  }

  _scheduleChromeLift() {
    if (this._liftRaf) return
    this._liftRaf = requestAnimationFrame(() => {
      this._liftRaf = null
      this._applyChromeLift()
    })
  }

  // Announce connect/disconnect so the engine re-measures the chrome strips.
  _announceChrome() {
    const selector = this.element.getAttribute("data-ui--flow-minimap-ui--flow-outlet")
    const canvasId = selector && selector.startsWith("#") ? selector.slice(1) : null
    document.dispatchEvent(new CustomEvent("ui--flow:chromechange", { detail: { canvasId } }))
  }

  // Null-safe outlet access: rAF-flushed renders may run AFTER disconnect (a
  // frame's worth of pending work survives), where the Stimulus outlet getter
  // throws "Missing outlet element". try/catch keeps the teardown silent.
  _flow() {
    try { return this.hasUiFlowOutlet ? this.uiFlowOutlet : null } catch (e) { return null }
  }

  _scheduleViewport() { this._flush("viewport") }

  // Geometry-only flush: recompute bounds + rect attrs from cached dims — no
  // DOM reads, no element rebuild. Flush kinds form a lattice
  // (full ⊃ geometry ⊃ viewport): while a frame flush is queued, a heavier
  // request upgrades the pending kind so a membership rebuild is never
  // swallowed by an already-scheduled geometry-only flush.
  _flush(kind) {
    if (this._pending) {
      const order = { "full": 3, "geometry": 2, "viewport": 1 }
      if (order[kind] > order[this._flushMode]) this._flushMode = kind
      return
    }
    this._pending = true
    this._flushMode = kind
    this._frameRaf = requestAnimationFrame(() => {
      const mode = this._flushMode
      this._pending = false
      this._frameRaf = null
      this._flushMode = null
      if (mode === "full") this._render()
      else if (mode === "geometry") this._applyGeometry()
      else this._renderViewportOnly()
    })
  }
  _scheduleRender() { this._flush("full") }
  _scheduleGeometry() { this._flush("geometry") }

  // Full render: refresh cached dims + bounds, rebuild the rect map in place
  // (create rects once per node, then mutate attributes — no innerHTML nukes).
  _render() {
    const flow = this._flow()
    if (!flow?.hasNodesTarget) return

    // A3: hidden nodes (display:none) get no minimap rect — the flow
    // outlet's _nodeVisible is the ONE predicate box-select and fitView
    // also use (computed style, so a class-hidden node counts exactly like
    // inline display:none), and the three views of the graph agree.
    // Filtering BEFORE seen.add means a node hidden after a previous render
    // falls out of `seen` here and the prune pass below removes its stale
    // rect on this same pass.
    const nodes = [...flow.nodesTarget.querySelectorAll("[data-ui--flow-node]")].filter(n => flow._nodeVisible(n))
    if (nodes.length === 0) {
      // Empty graph (FIX D): the old code early-returned here, so the rects
      // from the previous frame survived a delete-everything action (ghost
      // node rects + stale viewport box). Tear the layer down instead.
      this._nodeRects.forEach(rect => rect.remove())
      this._nodeRects.clear()
      this._nodeDims.clear()
      this._bounds = null
      this._vpRect?.remove()
      this._vpRect = null
      this._vpMask?.remove()
      this._vpMask = null
      this._svg?.remove()
      this._svg = null
      return
    }

    const mmW = this.element.offsetWidth || 200, mmH = this.element.offsetHeight || 150
    this._mmW = mmW
    this._mmH = mmH
    if (!this._svg) {
      this._svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
      this._svg.setAttribute("viewBox", `0 0 ${mmW} ${mmH}`)
      this.element.appendChild(this._svg)
    }
    this._svg.setAttribute("viewBox", `0 0 ${mmW} ${mmH}`)

    // Prune rects for nodes that no longer exist; create missing ones.
    const seen = new Set()
    nodes.forEach(n => {
      const id = n.dataset["ui-FlowNode"]
      seen.add(id)
      const { x, y } = this._nodeFlowPosition(n)
      this._nodeDims.set(id, { w: n.offsetWidth, h: n.offsetHeight, x, y })
      if (!this._nodeRects.has(id)) {
        const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect")
        rect.setAttribute("fill", this.nodeColorValue)
        rect.setAttribute("stroke", this.nodeStrokeColorValue)
        rect.setAttribute("stroke-width", "1")
        rect.setAttribute("rx", "2")
        this._nodeRects.set(id, rect)
        this._svg.insertBefore(rect, this._vpRect)
      }
    })
    this._nodeRects.forEach((rect, id) => {
      if (!seen.has(id)) { rect.remove(); this._nodeRects.delete(id); this._nodeDims.delete(id) }
    })

    // Membership render (W3-B OPT guard): rects just CREATED ship no
    // coordinates — the dirty fast path must not skip them. Null the cached
    // bounds so the geometry pass takes its full-rewrite branch and clear
    // any stale drag-era dirty ids (each is re-created above anyway).
    this._bounds = null
    this._dirtyNodes.clear()

    this._applyGeometry()
  }

  // _nodeData positions give bounds; cached dims mean zero offsetWidth reads
  // on the nodemove path — flowPosition stays JS-only.
  _applyGeometry() {
    if (this._nodeDims.size === 0) return
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    this._nodeDims.forEach(d => {
      minX = Math.min(minX, d.x); minY = Math.min(minY, d.y)
      maxX = Math.max(maxX, d.x + d.w); maxY = Math.max(maxY, d.y + d.h)
    })
    const pad = 50
    minX -= pad; minY -= pad; maxX += pad; maxY += pad
    const cw = maxX - minX, ch = maxY - minY
    const mmW = this.element.offsetWidth || 200, mmH = this.element.offsetHeight || 150
    const scale = Math.min(mmW / cw, mmH / ch)
    // Dirty fast path (W3-B OPT): unchanged bounds ⇒ unchanged scale ⇒ only
    // the moved nodes' rects can differ — a mid-drag nodemove inside the
    // existing extent rewrites 1 rect instead of 1-per-node. A bounds change
    // (moved node crossed the extent) invalidates the scale and rewrites
    // everything exactly like the pre-fast-path loop. Content is identical:
    // the same (d, minX, minY, scale) tuple feeds both writes.
    const boundsUnchanged = this._bounds &&
      this._bounds.minX === minX && this._bounds.minY === minY && this._bounds.scale === scale
    this._bounds = { minX, minY, scale }

    const writeRect = (d, id) => {
      const rect = this._nodeRects.get(id)
      if (!rect) return
      rect.setAttribute("x", String((d.x - minX) * scale))
      rect.setAttribute("y", String((d.y - minY) * scale))
      rect.setAttribute("width", String(Math.max(d.w * scale, 3)))
      rect.setAttribute("height", String(Math.max(d.h * scale, 3)))
    }
    if (boundsUnchanged && this._dirtyNodes.size > 0) {
      this._dirtyNodes.forEach(id => { const d = this._nodeDims.get(id); if (d) writeRect(d, id) })
    } else {
      this._nodeDims.forEach(writeRect)
    }
    this._dirtyNodes.clear()
    this._renderViewportOnly()
  }

  // Viewport-only mutation: with cached bounds this is one rect's attrs.
  _renderViewportOnly() {
    const flow = this._flow()
    if (!flow || !this._bounds) return
    const { minX, minY, scale } = this._bounds

    const rect = flow.element.getBoundingClientRect()
    const vpx = ((-flow.viewport.x / flow.viewport.zoom) - minX) * scale
    const vpy = ((-flow.viewport.y / flow.viewport.zoom) - minY) * scale
    const vpw = (rect.width / flow.viewport.zoom) * scale
    const vph = (rect.height / flow.viewport.zoom) * scale

    if (!this._vpRect) {
      this._vpRect = document.createElementNS("http://www.w3.org/2000/svg", "rect")
      this._vpRect.setAttribute("fill", this.viewportColorValue)
      this._vpRect.setAttribute("stroke", this.viewportStrokeValue)
      this._vpRect.setAttribute("stroke-width", "1")
      this._vpRect.setAttribute("rx", "2")
      this._svg?.appendChild(this._vpRect)
    }
    this._vpRect.setAttribute("x", String(vpx))
    this._vpRect.setAttribute("y", String(vpy))
    this._vpRect.setAttribute("width", String(vpw))
    this._vpRect.setAttribute("height", String(vph))

    // Viewport mask (UX audit): an evenodd overlay dims everything OUTSIDE
    // the viewport rect — the outer subpath is the whole minimap, the inner
    // subpath is the viewport hole. Sits above the node rects + viewport
    // rect; pointer-events stay off so panning is unaffected.
    if (!this._vpMask) {
      this._vpMask = document.createElementNS("http://www.w3.org/2000/svg", "path")
      this._vpMask.setAttribute("fill", "hsl(var(--overlay) / 0.25)")
      this._vpMask.setAttribute("fill-rule", "evenodd")
      this._vpMask.setAttribute("pointer-events", "none")
      this._svg?.appendChild(this._vpMask)
    }
    const mmW = this._mmW || this.element.offsetWidth || 200
    const mmH = this._mmH || this.element.offsetHeight || 150
    this._vpMask.setAttribute("d",
      `M0,0 H${mmW} V${mmH} H0 Z M${vpx},${vpy} H${vpx + vpw} V${vpy + vph} H${vpx} Z`)
  }

  // Flow-space position of a node element — mirror of flow.flowPosition with
  // the fallback the original code carried, kept for element-direct calls.
  _nodeFlowPosition(n) {
    if (this.uiFlowOutlet?.flowPosition) return this.uiFlowOutlet.flowPosition(n)
    return { x: parseFloat(n.style.left) || 0, y: parseFloat(n.style.top) || 0 }
  }

  // Absolute flow-space offset of a node's ANCESTOR chain (flowPosition minus
  // the element's own left/top): the amount a parent-relative position must
  // be shifted by to read absolute in _nodeDims terms.
  _ancestorFlowOffset(el) {
    let x = 0, y = 0
    let p = el.parentElement
    while (p && p.dataset && p.dataset["ui-FlowNode"]) {
      x += parseFloat(p.style.left) || 0
      y += parseFloat(p.style.top) || 0
      p = p.parentElement
    }
    return { x, y }
  }

  _handlePan(event) {
    event.stopPropagation()
    const flow = this._flow()
    if (!flow) return

    const move = (e) => {
      const rect = this.element.getBoundingClientRect()
      // S10: a zero-size minimap (hidden, unlaid-out) would divide by zero and
      // poison the engine viewport with NaN.
      if (!rect.width || !rect.height) return
      const px = (e.clientX - rect.left) / rect.width
      const py = (e.clientY - rect.top) / rect.height

      // Bounds reuse (perf): the cached node data + pad reproduces the old
      // re-scan without touching the DOM per pan frame.
      let mnX = Infinity, mnY = Infinity, mxX = -Infinity, mxY = -Infinity
      this._nodeDims.forEach(d => {
        mnX = Math.min(mnX, d.x); mnY = Math.min(mnY, d.y)
        mxX = Math.max(mxX, d.x + d.w); mxY = Math.max(mxY, d.y + d.h)
      })
      if (mnX === Infinity) return
      mnX -= 50; mnY -= 50; mxX += 50; mxY += 50

      const cw = mxX - mnX, ch = mxY - mnY
      const tx = mnX + px * cw, ty = mnY + py * ch
      const elRect = flow.element.getBoundingClientRect()
      const targetX = -tx * flow.viewport.zoom + elRect.width / 2
      const targetY = -ty * flow.viewport.zoom + elRect.height / 2
      if (!Number.isFinite(targetX) || !Number.isFinite(targetY)) return
      // S10: route through the engine's public pan API — direct
      // `flow.viewport` writes skipped viewportchange (the status bar and
      // hosts never learned about the pan), the translate-extent clamp and
      // the move gesture. panBy takes deltas and covers all three.
      flow.panBy(targetX - flow.viewport.x, targetY - flow.viewport.y)
      this._renderViewportOnly()
    }

    move(event)
    const up = () => { document.removeEventListener("pointermove", move); document.removeEventListener("pointerup", up) }
    document.addEventListener("pointermove", move)
    document.addEventListener("pointerup", up)
  }
}
